
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BackgroundExportStatus, downloadExportFile } from '@/utils/organization/backgroundExport';
import { isExportReady } from '@/utils/organization/exportStatusDisplay';
import { Download, AlertCircle, Clock, CheckCircle } from 'lucide-react';

interface UseExportNotificationsOptions {
  connectionId?: string;
  enabled?: boolean;
  includeReportExports?: boolean;
}

// Two listeners can receive the same realtime event (the page-level one in OrganisationProfile, which is unfiltered
// until a connection is chosen, and the Reports tab's report-export one), each with its own duplicate check. The first
// to see an export-status pair notifies; the other stays quiet. Entries expire, so the same pair can notify again later.
const SHARED_DEDUPE_MS = 30_000;
const recentlyNotified = new Set<string>();
const claimNotification = (key: string): boolean => {
  if (recentlyNotified.has(key)) return false;
  recentlyNotified.add(key);
  setTimeout(() => recentlyNotified.delete(key), SHARED_DEDUPE_MS);
  return true;
};

export const useExportNotifications = ({
  connectionId,
  enabled = true,
  includeReportExports = false
}: UseExportNotificationsOptions = {}) => {
  const { toast } = useToast();
  const notifiedExports = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) return;

    // Clear notification history when connection changes
    notifiedExports.current.clear();

    let channel: any;

    const setupRealtime = () => {
      const channelName = includeReportExports
        ? 'exports-notifications-reports'
        : connectionId
          ? `exports-notifications-${connectionId}`
          : 'exports-notifications-global';

      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'csv_exports',
            ...(includeReportExports && { filter: 'connection_id=like.report-%' }),
            ...(connectionId && !includeReportExports && { filter: `connection_id=eq.${connectionId}` })
          },
          (payload: any) => {

            if (payload.new) {
              const exportStatus = payload.new as BackgroundExportStatus;
              handleExportStatusChange(exportStatus);
            }
          }
        )
        .subscribe((status) => { });
    };

    const isRetryStep = (currentStep?: string): boolean => {
      if (!currentStep) return false;

      const retrySteps = [
        'retrying_chunk_generation',
        'retry_chunk',
        'chunk_retry_attempt'
      ];

      return retrySteps.some(step => currentStep.toLowerCase().includes(step.toLowerCase()));
    };

    const isFinalFailure = (exportStatus: BackgroundExportStatus): boolean => {
      if (exportStatus.status !== 'failed') return false;

      // Check if this is a final failure (not a chunk retry)
      const finalFailureSteps = [
        'file_generation_failed',
        'database_connection_failed',
        'query_execution_failed',
        'max_retries_exceeded',
        'export_timeout'
      ];

      return finalFailureSteps.some(step =>
        exportStatus.failure_step?.toLowerCase().includes(step.toLowerCase())
      );
    };

    const handleExportStatusChange = (exportStatus: BackgroundExportStatus) => {
      const exportId = exportStatus.export_id;
      const status = exportStatus.status;
      const currentStep = exportStatus.current_step;
      const exportKey = `${exportId}-${status}`;

      // Skip notifications for retry steps
      if (isRetryStep(currentStep)) {
        return;
      }

      // Skip chunk processing updates
      if (currentStep === 'PROCESSING_CHUNKS' || currentStep?.includes('PROCESSED_CHUNK')) {
        return;
      }

      // For failed status, only show if it's a final failure
      if (status === 'failed' && !isFinalFailure(exportStatus)) {
        return;
      }

      // A completed export without its file yet notifies nothing, so it must not use up the duplicate check:
      // the update that makes it ready has to be able to notify.
      if (status === 'completed' && !isExportReady(exportStatus)) {
        return;
      }

      // Avoid duplicate notifications for the same export-status combination
      if (notifiedExports.current.has(exportKey)) {
        return;
      }

      notifiedExports.current.add(exportKey);

      // Another listener on the page got this event first: it has notified already.
      if (!claimNotification(exportKey)) {
        return;
      }

      switch (status) {
        case 'processing':
          toast({
            title: "Export Started",
            description: `Processing export for ${exportStatus.table_name}...`,
            variant: "default"
          });
          break;

        case 'completed':
          if (!isExportReady(exportStatus)) {
            break;
          }
          const rowCountText = exportStatus.actual_row_count !== null && exportStatus.actual_row_count !== undefined
            ? `(${exportStatus.actual_row_count.toLocaleString()} rows)`
            : '';
          toast({
            title: "Export Completed",
            description: `${exportStatus.table_name} export is ready for download ${rowCountText}`.trim(),
            variant: "default"
          });
          break;

        case 'failed':
          toast({
            title: "Export Failed",
            description: `Export for ${exportStatus.table_name} failed permanently: ${exportStatus.error_message || 'Unknown error'}`,
            variant: "destructive"
          });
          break;

        default:
          break
      }
    };

    setupRealtime();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [connectionId, enabled, toast]);

  const clearNotificationHistory = () => {
    notifiedExports.current.clear();
  };

  return {
    clearNotificationHistory
  };
};